import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Button } from "components/ui/button";
import { Loader2 } from 'lucide-react';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';

/**
 * ConfirmationModal Component
 *
 * A reusable modal dialog that prompts users to confirm or cancel an action.
 * Built using AlertDialog components for accessibility and consistent styling.
 *
 * Features:
 * - Controlled visibility state
 * - Customizable title and description
 * - Standardized cancel and confirm buttons
 * - Accessible design with proper ARIA attributes
 * - High z-index to ensure visibility above other UI elements
 * - Responsive layout with maximum width
 *
 * Props:
 * @param {boolean} open - Controls whether the modal is displayed
 * @param {(open: boolean) => void} onOpenChange - Callback when modal open state changes
 * @param {string} title - The title displayed in the modal header
 * @param {string} description - The descriptive text explaining the action to confirm
 * @param {() => void} onConfirm - Callback function executed when the user confirms the action
 *
 * @returns {JSX.Element} A confirmation modal dialog component
 *
 * @example
 * // Basic usage
 * <ConfirmationModal
 *   open={showDeleteDialog}
 *   onOpenChange={setShowDeleteDialog}
 *   title="Delete Item"
 *   description="Are you sure you want to delete this item? This action cannot be undone."
 *   onConfirm={handleDelete}
 * />
 */

interface ConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string | React.ReactNode;
  onConfirm: () => Promise<void> | void;
  confirmText?: string | React.ReactNode;
  cancelText?: string;
  /**
   * If true, the modal will not automatically close after confirmation.
   * The parent component is responsible for closing the modal if needed.
   * Default is false (modal will auto-close after confirmation).
   */
  preventAutoClose?: boolean;
  isLoading?: boolean;
  confirmVariant?: 'default' | 'delete';
  /** Raise above Settings and nested Dialog layers (z-110–130). */
  nested?: boolean;
  bgBlurred?: boolean;
  highlightedText?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onOpenChange,
  title,
  description,
  onConfirm,
  confirmText,
  cancelText,
  preventAutoClose = false,
  isLoading = false,
  confirmVariant = 'default',
  nested = false,
  bgBlurred = false,
  highlightedText,
}) => {
  const { t } = useTranslation();
  const resolvedConfirmText = confirmText ?? t('CONFIRM', 'Confirm');
  const resolvedCancelText = cancelText ?? t('CANCEL', 'Cancel');
  const handleConfirmClick = async () => {
    await onConfirm();
    if (!preventAutoClose) {
      onOpenChange(false);
    }
  };

  const handleCancelClick = () => {
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent nested={nested} bgBlurred={bgBlurred} aria-describedby="alert-dialog-description">
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription className="whitespace-pre-line">
            {description}
            {highlightedText ? (
              <>
                {' - '}
                <strong className="font-bold text-foreground">{highlightedText}</strong>
              </>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="ghost"
            onClick={handleCancelClick}
            className="h-8 rounded-[6px] px-4 text-sm font-medium text-foreground shadow-none hover:bg-transparent hover:text-foreground dark:text-foreground dark:hover:bg-transparent dark:hover:text-foreground"
          >
            {resolvedCancelText}
          </Button>
          <Button
            variant={confirmVariant === 'delete' ? 'destructive' : 'default'}
            className={cn(
              'h-8 rounded-[6px] px-4 text-sm font-semibold',
              confirmVariant === 'delete' &&
              'border border-red-500 bg-red-500/10 text-red-600 shadow-none hover:bg-red-500/15 dark:border-red-500 dark:bg-red-500/10 dark:text-red-400 dark:hover:bg-red-500/20'
            )}
            onClick={handleConfirmClick}
            disabled={isLoading}
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {resolvedConfirmText}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmationModal;
